import React from 'react';

import type { VisualDiffSegment } from '@aline/common';
import type { Theme } from '@mui/material/styles';
import { useTheme } from '@mui/material/styles';
import { previewCardStyles, suggestTextStyles } from 'src/utils/preview-styles';
import { makeStyles } from 'tss-react/mui';

import { RedlinePreviewSection } from './ExpandableRedlineCard';

const useStyles = makeStyles()(theme => ({
  diffContainer: {
    ...previewCardStyles(theme),
    fontFamily: 'inherit',
    whiteSpace: 'pre-wrap',
  },
  // Same text treatment as the card, without the box around it.
  plainDiffContainer: {
    fontFamily: 'inherit',
    fontSize: theme.typography.body2.fontSize,
    lineHeight: 1.6,
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
  },
}));

interface RedlineActionVisualDiffProps {
  visualDiffSegments: VisualDiffSegment[];
  /**
   * Drop the bordered card treatment around the diff, for hosts that already
   * put the diff inside a box of their own (the playbook panel's suggestion
   * units); chat keeps the default.
   */
  plain?: boolean;
  /**
   * The section heading above the diff. Null renders the bare diff, for
   * hosts whose surrounding card already labels it (the playbook panel's
   * suggestion units); chat keeps the default.
   */
  title?: string | null;
}

/**
 * Renders a redline action's visual diff using pre-computed VisualDiffSegment data.
 * Styling matches the Aline Editor's suggested edit rendering:
 *
 * - oldInsert: blue text + underline (existing user edit)
 * - oldDelete: red text + strikethrough (existing user edit)
 * - newInsert: blue text + underline + light blue background (AI suggestion)
 * - newDelete: red text + strikethrough + light pink background (AI suggestion)
 */
export function getSegmentStyle(
  segment: VisualDiffSegment,
  appColors: Theme['appColors']
): React.CSSProperties {
  const style: React.CSSProperties = {};

  // Insertions: blue text + underline, matching the Aline Editor's suggestInsert styling
  if (segment.oldInsert || segment.newInsert) {
    style.color = appColors.suggestInsert;
    style.borderBottom = `1.5px solid ${appColors.suggestInsert}`;
  }

  // Deletions: red text + strikethrough, matching the Aline Editor's suggestRemove styling
  if (segment.oldDelete || segment.newDelete) {
    Object.assign(style, suggestTextStyles(appColors, 'remove'));
  }

  // AI-suggested segments get a tinted background so they're visually distinct
  // from the user's pre-existing edits. newDelete wins when both are set
  // (the AI's final action is the delete).
  if (segment.newDelete) {
    style.backgroundColor = appColors.suggestRemoveBackground;
  } else if (segment.newInsert) {
    style.backgroundColor = appColors.suggestInsertBackground;
  }

  return style;
}

export const RedlineActionVisualDiff = React.memo(function RedlineActionVisualDiff({
  plain = false,
  title = 'Change:',
  visualDiffSegments,
}: RedlineActionVisualDiffProps): JSX.Element {
  const { classes } = useStyles();
  const theme = useTheme<Theme>();

  const diff = (
    <div className={plain ? classes.plainDiffContainer : classes.diffContainer}>
      {visualDiffSegments.map((segment, index) => (
        <span key={index} style={getSegmentStyle(segment, theme.appColors)}>
          {segment.text}
        </span>
      ))}
    </div>
  );
  if (title === null) return diff;

  return <RedlinePreviewSection title={title}>{diff}</RedlinePreviewSection>;
});
